import { DeleteOutlined } from "@ant-design/icons";
import { Button, Modal } from "antd";
import { format } from "date-fns";
import React, { useState } from "react";
import { useNotification } from "../../contexts/Notifications";
import { Scope, useDeleteBatchMutation } from "../../generated/types";
import { Batch } from "../../types";
import { DeleteConfirmationModal } from "../DeleteConfirmationModal/DeleteConfirmationModal";

export const DELETE_BATCH_ALERT =
  "This permanently removes the batch and all of its shots, arrows, progresses and spotlight positions.";
export const EXTERNAL_DELETE_NOTE =
  "This is released data. The batch comes back on the next release unless it is also deleted from internal.";
export const INTERNAL_POSITIONS_NOTE =
  "Spotlight markers placed in this week are stored once for both internal and external, so external loses its markers for this week immediately even though its batch stays until the next release.";

// Matches the GraphQL error code thrown by the backend deleteBatch resolver.
const SPOTLIGHTS_ATTACHED_CODE = "SPOTLIGHTS_ATTACHED";

type Props = {
  customer: string;
  project: string;
  scope: Scope;
  batch: Batch;
  onDeleted: () => void;
};

export const DeleteBatchButton = (props: Props) => {
  const { customer, project, scope, batch, onDeleted } = props;
  const notify = useNotification();
  const [visible, setVisible] = useState(false);
  // Hook form of Modal: static methods no-op under React 19 (see PlansHeader).
  const [modalApi, modalContextHolder] = Modal.useModal();

  const [deleteBatch] = useDeleteBatchMutation({
    update: (cache) => {
      cache.evict({
        id: cache.identify({ __typename: "Batch", id: batch.id }),
      });
      cache.gc();
    },
    onCompleted: () => {
      notify(`Deleted batch week ${batch.weekNumber} from ${scope}`, "success");
      onDeleted();
    },
    onError: (error) => {
      const spotlightError = error.graphQLErrors.find(
        (e) => e.extensions?.code === SPOTLIGHTS_ATTACHED_CODE,
      );
      if (spotlightError) {
        modalApi.error({
          title: "Cannot delete batch",
          content: (
            <>
              {spotlightError.message.split("\n").map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </>
          ),
        });
        return;
      }
      notify(
        `Failed to delete batch week ${batch.weekNumber} from ${scope} - err: ${error}`,
        "error",
      );
    },
  });

  const onOk = async () => {
    await deleteBatch({
      variables: { customer, project, scope, batchId: batch.id },
    });
  };

  const modalBody = (
    <>
      <p>
        Delete the <b>{scope}</b> batch for week <b>{batch.weekNumber}</b>{" "}
        captured on <b>{format(new Date(batch.timestamp), "yyyy/MM/dd")}</b>?
      </p>
      {scope === Scope.External && <p>{EXTERNAL_DELETE_NOTE}</p>}
      {scope === Scope.Internal && <p>{INTERNAL_POSITIONS_NOTE}</p>}
    </>
  );

  return (
    <>
      <Button
        danger
        type="text"
        icon={<DeleteOutlined />}
        data-testid="delete-batch-button"
        aria-label="Delete batch"
        onClick={() => setVisible(true)}
      />
      <DeleteConfirmationModal
        title={`Delete batch week ${batch.weekNumber} (${scope})?`}
        visible={visible}
        setVisible={setVisible}
        onOk={onOk}
        deletionText={String(batch.weekNumber)}
        modalBody={modalBody}
        alertText={DELETE_BATCH_ALERT}
      />
      {modalContextHolder}
    </>
  );
};
